import {
  Handle,
  Position,
  OnConnect,
  useStore,
  Node,
  Edge,
} from '@xyflow/react';

export type HandleItem = {
  id: string;
  label: string;
};

type HandlesWrapperProps = {
  sources?: HandleItem[];
  targets?: HandleItem[];
  newOutputs?: HandleItem[];
  onSourceAttach?: OnConnect;
  onTargetAttach?: OnConnect;
};

const DEFAULT_TOP_OFFSET = 80;
const HANDLE_SPACING = 30;

const rightStyles = (offset: number) => ({
  position: 'absolute',
  top: DEFAULT_TOP_OFFSET + offset * HANDLE_SPACING,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row-reverse',
});

const leftStyles = (offset: number) => ({
  position: 'absolute',
  top: DEFAULT_TOP_OFFSET + offset * HANDLE_SPACING,
  left: 0,
  display: 'flex',
  alignItems: 'center',
});

const HandlesWrapper = ({
  sources = [],
  targets = [],
  newOutputs = [],
  onSourceAttach,
  onTargetAttach,
}: HandlesWrapperProps) => {
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);
  const selectedNodes = nodes.filter((node) => node.selected);
  const selectedEdges = edges.filter((edge) => edge.selected);

  const highlightedEdges = edges.filter((edge) => {
    const selectedNodeIds = selectedNodes.map((node) => node.id);
    return selectedNodeIds.some(
      (nodeId) => edge.source === nodeId || edge.target === nodeId
    );
  });

  const getHandleState = (handleId: string, type: 'source' | 'target') => {
    const isConnected = edges.some((edge) =>
      type === 'source'
        ? edge.sourceHandle === handleId
        : edge.targetHandle === handleId
    );

    const isSelected = selectedEdges.some((edge) =>
      type === 'source'
        ? edge.sourceHandle === handleId
        : edge.targetHandle === handleId
    );

    const isHighlighted = highlightedEdges.some((edge) =>
      type === 'source'
        ? edge.sourceHandle === handleId
        : edge.targetHandle === handleId
    );

    return {
      connected: isConnected,
      selected: isSelected,
      highlighted: isHighlighted,
    };
  };

  const getHandleClasses = (handleId: string, type: 'source' | 'target') => {
    const state = getHandleState(handleId, type);
    const baseClass =
      type === 'source'
        ? 'react-flow__handle-source'
        : 'react-flow__handle-target';
    const classes = [baseClass];

    if (state.connected) classes.push('connected');
    if (state.selected) classes.push('selected');
    if (state.highlighted) classes.push('highlighted');

    return classes.join(' ');
  };

  return (
    <>
      {sources.map((input, index) => (
        <div
          key={`input-${index}`}
          style={leftStyles(index) as React.CSSProperties}
        >
          <Handle
            type='target'
            position={Position.Left}
            id={input.id}
            isConnectable={true}
            onConnect={onTargetAttach}
            className={getHandleClasses(input.id, 'target')}
          />
          <span style={{ marginLeft: 8, fontSize: 12 }}>{input.label}</span>
        </div>
      ))}
      {targets.map((output, index) => (
        <div
          key={`output-${index}`}
          style={rightStyles(index) as React.CSSProperties}
        >
          <Handle
            type='source'
            position={Position.Right}
            id={output.id}
            isConnectable={true}
            onConnect={onSourceAttach}
            className={getHandleClasses(output.id, 'source')}
          />
          <span style={{ marginRight: 8, fontSize: 12 }}>{output.label}</span>
        </div>
      ))}
      {newOutputs?.map((output, index) => (
        <div
          key={`output-new-${index}`}
          style={rightStyles(targets.length + index) as React.CSSProperties}
        >
          <Handle
            type='source'
            position={Position.Right}
            id={`${output.id}-${targets.length + (index + 1)}`}
            isConnectable={true}
            onConnect={onSourceAttach}
            className={getHandleClasses(
              `${output.id}-${targets.length + (index + 1)}`,
              'source'
            )}
          />
          <span style={{ marginRight: 8, fontSize: 12 }}>{output.label}</span>
        </div>
      ))}
    </>
  );
};

export default HandlesWrapper;
